/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.3
 * Description: Main layout component with navigation, sidebar, and Dolly the Sheep Easter egg
 */

import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  FlaskConical,
  Layers,
  Check,
  Menu,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useAuth } from '@context/AuthContext'
import { useUserContext } from '@context/UserContext'
import { useTheme } from '@context/ThemeContext'
import { api } from '@services/api'
import { toast } from 'react-toastify'
import FloatingChat from '@components/FloatingChat'
import { NAV_REGISTRY, type NavEntry, type StandaloneNavEntry, type GroupNavEntry } from '@config/navRegistry'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { activeContext, availableContexts, switchContext } = useUserContext()
  const { mode, setMode } = useTheme()
  const [isRainingDolly, setIsRainingDolly] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['systems', 'integration'])
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isTestChatOpen, setIsTestChatOpen] = useState(false)
  const [testAssistantAvailable, setTestAssistantAvailable] = useState(false)
  const [isContextOpen, setIsContextOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 768)
  const contextRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextRef.current && !contextRef.current.contains(e.target as Node)) {
        setIsContextOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) setIsSidebarOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLogout = async () => {
    try {
      // Start the Dolly rain animation
      setIsRainingDolly(true)

      // Wait 2 seconds for animation to complete
      setTimeout(async () => {
        await logout()
        toast.success('Logged out successfully')
        setIsRainingDolly(false)
        navigate('/login')
      }, 2000)
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Logout failed')
      setIsRainingDolly(false)
    }
  }

  const contextName = activeContext?.name ?? ''

  // AI Test Assistant is opt-in per-user (role lookup or manual allow-list on the
  // backend) — fetch once so the toggle only renders for accounts that have it.
  useEffect(() => {
    let cancelled = false
    api
      .getChatStatus()
      .then((status) => {
        if (!cancelled) setTestAssistantAvailable(status.testAssistantAvailable ?? false)
      })
      .catch(() => {
        // Fail closed (hidden) rather than surface an error for an opt-in feature
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Active digest jobs, polled app-wide so ingestion stays visible from any
  // screen — a long run is easy to forget once you navigate off Data Digest.
  // Polls slowly (10s) while something is live and backs off to a quiet 60s
  // check otherwise, so an idle session costs almost nothing.
  const [activeDigestJobs, setActiveDigestJobs] = useState(0)

  useEffect(() => {
    if (!activeContext) return
    let cancelled = false

    const check = async () => {
      try {
        const { jobs } = await api.getDigestJobs(50)
        if (cancelled) return
        setActiveDigestJobs(jobs.filter((j) => j.status === 'pending' || j.status === 'processing').length)
      } catch {
        // Never surface nav-badge failures — the Data Digest page reports
        // properly, and a badge is not worth a toast.
      }
    }

    check()
    const id = setInterval(check, activeDigestJobs > 0 ? 10_000 : 60_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [activeContext, activeDigestJobs])

  const menuItems: NavEntry[] = NAV_REGISTRY.filter(
    (e) => e.visibleIn.includes('*') || e.visibleIn.includes(contextName),
  )

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => (prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]))
  }

  const cycleTheme = () => {
    setMode(mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system')
  }
  const ThemeIcon = mode === 'system' ? Monitor : mode === 'light' ? Sun : Moon
  const themeLabel = mode === 'system' ? 'Theme: System' : mode === 'light' ? 'Theme: Light' : 'Theme: Dark'

  return (
    <div className="flex min-h-screen flex-col bg-slate-100 dark:bg-gray-900">
      <div className="flex flex-1">
        {/* Mobile sidebar backdrop */}
        {isMobile && isSidebarOpen && (
          <div className="fixed inset-0 z-10 bg-black/30" onClick={() => setIsSidebarOpen(false)} />
        )}
        {/* Sidebar */}
        <aside
          className={`fixed top-0 bottom-0 left-0 z-20 flex flex-col overflow-hidden border-r border-gray-200 bg-white transition-all duration-300 dark:border-gray-700 dark:bg-gray-800 ${isSidebarOpen ? 'w-72' : 'w-16'}`}
        >
          {/* Header */}
          <div
            className={`flex flex-shrink-0 items-center border-b border-gray-200 p-4 dark:border-gray-700 ${isSidebarOpen ? 'space-x-3' : 'justify-center'}`}
          >
            <button
              onClick={() => setIsSidebarOpen((o) => !o)}
              className="flex-shrink-0 rounded-lg p-1 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              title={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <Menu className="h-5 w-5" />
            </button>
            {isSidebarOpen && (
              <div className="flex cursor-pointer items-center space-x-3" onClick={() => navigate('/')}>
                <img
                  src="/dolly.png"
                  alt="Dolly the Sheep"
                  className="relative h-8 w-8 origin-top-left"
                  title="Dolly the Sheep - Click to go home!"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">DAL-ify</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Digital Abstraction Layer</p>
                </div>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <nav
            className={`flex-1 overflow-hidden overflow-y-auto p-4 ${isSidebarOpen ? 'space-y-2' : 'flex flex-col items-center space-y-2'}`}
          >
            {isSidebarOpen ? (
              <>
                {/* Context Switcher */}
                <div className="relative" ref={contextRef}>
                  <p className="mb-1 text-xs font-semibold text-gray-400 uppercase dark:text-gray-500">
                    {availableContexts.length === 1 ? 'Context' : 'Select Context'}
                  </p>
                  <button
                    onClick={() => availableContexts.length > 1 && setIsContextOpen((o) => !o)}
                    disabled={availableContexts.length <= 1}
                    className="flex w-full items-center justify-between space-x-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-gray-50 dark:border-gray-700 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:hover:bg-gray-900"
                    title={
                      availableContexts.length <= 1 ? (activeContext?.display_name ?? 'No context') : 'Switch context'
                    }
                  >
                    <div className="flex items-center space-x-1">
                      <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="ml-1 max-w-50 truncate font-medium">
                        {activeContext?.display_name ?? 'No context'}
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-end text-gray-400 transition-transform dark:text-gray-500 ${isContextOpen ? 'rotate-180' : ''} ${availableContexts.length <= 1 ? 'invisible' : ''}`}
                    />
                  </button>

                  {isContextOpen && (
                    <div className="absolute top-full right-0 z-50 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
                      <p className="px-3 py-1.5 text-xs font-semibold tracking-wide text-gray-400 uppercase dark:text-gray-500">
                        Your Contexts
                      </p>
                      {availableContexts
                        .filter((ctx) => ctx.name !== 'superadmin')
                        .map((ctx) => (
                          <button
                            key={ctx.id}
                            onClick={() => {
                              switchContext(ctx.id)
                              setIsContextOpen(false)
                            }}
                            className="flex w-full items-center justify-between px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <span>{ctx.display_name}</span>
                            {activeContext?.id === ctx.id && (
                              <Check className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                            )}
                          </button>
                        ))}
                      {availableContexts.some((ctx) => ctx.name === 'superadmin') && (
                        <>
                          <hr className="my-1 border-gray-200 dark:border-gray-700" />
                          {availableContexts
                            .filter((ctx) => ctx.name === 'superadmin')
                            .map((ctx) => (
                              <button
                                key={ctx.id}
                                onClick={() => {
                                  switchContext(ctx.id)
                                  setIsContextOpen(false)
                                }}
                                className="flex w-full items-center justify-between px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                <span className="font-bold">{ctx.display_name}</span>
                                {activeContext?.id === ctx.id && (
                                  <Check className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                )}
                              </button>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <hr className="text-gray-200 dark:border-gray-700 dark:text-gray-700" />

                {menuItems.length > 0 ? (
                  <>
                    {menuItems.map((item) => {
                      const Icon = item.icon

                      // Check if item has sub-items
                      if ('subItems' in item && item.subItems) {
                        const groupItem = item as GroupNavEntry
                        const isExpanded = expandedMenus.includes(groupItem.key)
                        const isAnySubItemActive = groupItem.subItems.some(
                          (subItem) => location.pathname === subItem.path,
                        )

                        return (
                          <div key={groupItem.key}>
                            {/* Parent Menu Item */}
                            <button
                              onClick={() => toggleMenu(groupItem.key)}
                              className={`flex w-full cursor-pointer items-center justify-between rounded-lg px-4 py-3 transition-colors ${
                                isAnySubItemActive
                                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 font-semibold'
                                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <Icon className="h-5 w-5" />
                                <span>{item.label}</span>
                              </div>
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>

                            {/* Sub Menu Items */}
                            {isExpanded && (
                              <div className="mt-1 ml-4 space-y-1">
                                {groupItem.subItems.map((subItem) => {
                                  const SubIcon = subItem.icon
                                  const isActive = location.pathname === subItem.path

                                  return (
                                    <button
                                      key={subItem.path}
                                      onClick={() => navigate(subItem.path)}
                                      className={`flex w-full items-center space-x-3 rounded-lg px-4 py-2 transition-colors ${
                                        isActive
                                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 font-medium'
                                          : 'cursor-pointer text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      <SubIcon className="h-4 w-4" />
                                      <span className="text-sm">{subItem.label}</span>
                                      {subItem.path === '/integration/context-digester' && activeDigestJobs > 0 && (
                                        <span
                                          className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                                          title={`${activeDigestJobs} digest job${activeDigestJobs === 1 ? '' : 's'} queued or running`}
                                        >
                                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-600" />
                                          {activeDigestJobs}
                                        </span>
                                      )}
                                    </button>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }

                      // Regular menu item without sub-items
                      const standaloneItem = item as StandaloneNavEntry
                      const isActive = location.pathname === standaloneItem.path
                      return (
                        <button
                          key={standaloneItem.path}
                          onClick={() => navigate(standaloneItem.path)}
                          className={`flex w-full items-center space-x-3 rounded-lg px-4 py-3 transition-colors ${
                            isActive
                              ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 font-semibold'
                              : 'cursor-pointer text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          <span>{item.label}</span>
                        </button>
                      )
                    })}
                  </>
                ) : (
                  <div className="px-4 py-10 text-center">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Coming soon</p>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      {activeContext?.display_name ?? 'This workspace'} is under construction.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {menuItems.flatMap((item) => {
                  const Icon = item.icon
                  if ('subItems' in item && item.subItems) {
                    return item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon
                      const isActive = location.pathname === subItem.path
                      return (
                        <button
                          key={subItem.path}
                          onClick={() => navigate(subItem.path)}
                          title={subItem.label}
                          className={`rounded-lg p-2 transition-colors ${isActive ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                        >
                          <SubIcon className="h-5 w-5" />
                        </button>
                      )
                    })
                  }
                  const standaloneItem = item as StandaloneNavEntry
                  const isActive = location.pathname === standaloneItem.path
                  return [
                    <button
                      key={standaloneItem.path}
                      onClick={() => navigate(standaloneItem.path)}
                      title={standaloneItem.label}
                      className={`rounded-lg p-2 transition-colors ${isActive ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                    >
                      <Icon className="h-5 w-5" />
                    </button>,
                  ]
                })}
              </>
            )}
          </nav>

          {/* User & AI Buttons */}
          <div className="border-t border-gray-200 p-2 dark:border-gray-700" ref={userMenuRef}>
            {isSidebarOpen ? (
              <div className="relative">
                {/* Theme Toggle */}
                <button
                  onClick={cycleTheme}
                  className="flex w-full cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  title={themeLabel}
                  aria-label={themeLabel}
                >
                  <ThemeIcon className="h-4 w-4" />
                  <span className="font-medium">{themeLabel}</span>
                </button>

                {/* AI Assistant Button */}
                <button
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  className={`flex w-full cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isChatOpen
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                  title="KT AI Assistant"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span className="font-medium">AI Assistant</span>
                </button>

                {/* AI Test Assistant Button — optional, only for test-allowed accounts */}
                {testAssistantAvailable && (
                  <button
                    onClick={() => setIsTestChatOpen(!isTestChatOpen)}
                    className={`flex w-full cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isTestChatOpen
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    title="AI Test Assistant"
                  >
                    <FlaskConical className="h-4 w-4" />
                    <span className="font-medium">AI Test Assistant</span>
                  </button>
                )}

                <button
                  onClick={() => setIsUserMenuOpen((o) => !o)}
                  className="flex w-full cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  title="User menu"
                >
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    <span className="flex-1 truncate text-left font-medium">{user?.name || user?.email}</span>
                  </div>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute bottom-full left-0 z-50 mb-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
                    <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.name}</p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false)
                        handleLogout()
                      }}
                      className="flex w-full items-center space-x-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
                {/* Internal Use Only Notice */}
                <div className="mt-2 flex items-center justify-center rounded border border-red-300 bg-red-100 px-3 py-1 dark:border-red-800 dark:bg-red-950/40">
                  <p className="text-xs font-semibold tracking-wide text-red-800 uppercase dark:text-red-300">
                    Internal Use Only
                  </p>
                </div>
              </div>
            ) : (
              <div className="relative flex flex-col items-center space-y-1">
                <button
                  onClick={cycleTheme}
                  title={themeLabel}
                  aria-label={themeLabel}
                  className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <ThemeIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  title="AI Assistant"
                  className={`rounded-lg p-2 transition-colors ${isChatOpen ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
                {testAssistantAvailable && (
                  <button
                    onClick={() => setIsTestChatOpen(!isTestChatOpen)}
                    title="AI Test Assistant"
                    className={`rounded-lg p-2 transition-colors ${isTestChatOpen ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  >
                    <FlaskConical className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={`min-w-0 flex-1 p-4 transition-all duration-300 sm:p-6 lg:p-8 ${isMobile ? 'ml-16' : isSidebarOpen ? 'ml-72' : 'ml-16'}`}
        >
          {children}
        </main>
      </div>

      {/* Floating Chat Widget */}
      <FloatingChat isOpen={isChatOpen} onToggle={() => setIsChatOpen(!isChatOpen)} />

      {/* Floating AI Test Assistant — independent session, only mounted for test-allowed accounts */}
      {testAssistantAvailable && (
        <FloatingChat isOpen={isTestChatOpen} onToggle={() => setIsTestChatOpen(!isTestChatOpen)} variant="test" />
      )}

      {/* Dolly Rain Animation */}
      {isRainingDolly && (
        <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden">
          <style>{`
            @keyframes dollyFall {
              0% {
                transform: translateY(-100px) rotate(0deg);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh) rotate(360deg);
                opacity: 0.8;
              }
            }
            .dolly-rain {
              position: absolute;
              width: 60px;
              height: 60px;
              animation: dollyFall 2s linear forwards;
            }
          `}</style>
          {Array.from({ length: 20 }).map((_, index) => (
            <img
              key={index}
              src="/dolly.png"
              alt="Dolly the Sheep"
              className="dolly-rain"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1.5 + Math.random()}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Layout
